/**
 * Non-Objectives Building Block Tab — Vision, Mission, Peak Ambition.
 *
 * Same Consensus Builder pattern as ObjectivesBBTab but:
 *   - No time periods (single static period key per BB type)
 *   - Contributions are raw prose (no WMBT parsing)
 *   - Team draft is prose text (vision/mission) or structured JSON (peakAmbition)
 *   - Synthesis uses ---CONTENT--- separator
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Button, Modal, DatePicker, Input, message as antdMessage, Tooltip, Radio } from "antd";
import {
  EyeOff,
  Lock,
  Unlock,
  Sparkles,
  Loader2,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Users,
  Download,
  Maximize2,
  Trash2,
  RotateCcw,
  FileText,
  Pencil,
  Check,
} from "lucide-react";
import { useMicRecorder, RecordingObjectType } from "@vojomo-core/shared";
import { VoInput } from "components/VoInput/VoInput";
import { TeamProseCollabEditor } from "./TeamProseCollabEditor";
import { PeakAmbitionCollabEditor } from "./PeakAmbitionCollabEditor";
import type { CollaborativeProseEditorHandle } from "@vojomo-core/shared/src/components/collab/CollaborativeProseEditor";
import { ConsensusBuilderBar, type CommandCenterConfig } from "components/ConsensusBuilder/ConsensusBuilderBar";
import { renderMarkdown } from "utils/renderMarkdown";
import { exportTarPdf } from "utils/tarPdfExport";
import type { AllBuildingBlocks, PeakAmbition, VisibilityMap } from "../types";
import { isVisibleForPeriod } from "../types";
import dayjs from "dayjs";

const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";

// ── Config ──────────────────────────────────────────────────────────────────

export interface ProseBBConfig {
  bbType: string;
  blockKey: keyof AllBuildingBlocks;
  periodType: string;
  bbLabel: string;
  contentType: "prose" | "structured";
  contributionPlaceholder: string;
  editPlaceholder: string;
}

export const PROSE_BB_CONFIGS: Record<string, ProseBBConfig> = {
  vision: {
    bbType: "vision",
    blockKey: "vision",
    periodType: "bb_vision",
    bbLabel: "Vision",
    contentType: "prose",
    contributionPlaceholder: "Or type your proposed vision statement...",
    editPlaceholder: "Or type edits to the team vision...",
  },
  mission: {
    bbType: "mission",
    blockKey: "mission",
    periodType: "bb_mission",
    bbLabel: "Mission",
    contentType: "prose",
    contributionPlaceholder: "Or type your proposed mission statement...",
    editPlaceholder: "Or type edits to the team mission...",
  },
  peakAmbition: {
    bbType: "peakAmbition",
    blockKey: "peakAmbition",
    periodType: "bb_peak_ambition",
    bbLabel: "Peak Ambition",
    contentType: "structured",
    contributionPlaceholder: "Or type your proposed peak ambition...",
    editPlaceholder: "Or type edits to the team peak ambition...",
  },
};

// ── Types ───────────────────────────────────────────────────────────────────

interface Contribution {
  id: string;
  sessionId: string;
  periodKey: string;
  bbType: string;
  contributorUserId: string;
  contributorName: string;
  rawNotes: string;
  submittedAt: string | null;
  updatedAt: string | null;
}

interface PeriodMeta {
  id: string;
  sessionId: string;
  periodKey: string;
  deadline: string | null;
  message: string;
  visibilityOpen: boolean;
  synthesisReleased: boolean;
  synthesisSummary: { reportId: string; generatedAt: string } | null;
  synthesisReports?: { reportId: string; generatedAt: string }[];
}

interface Synthesis {
  id: string;
  periodKey: string;
  reportMarkdown: string;
  teamDraft: string | Record<string, string> | null;
  generatedAt: string;
}

interface ProseVersion {
  version: number;
  draft: string | Record<string, string>;
  source: string;
  reportId?: string;
  createdAt: string;
  editedAt?: string;
}

// ── Props ───────────────────────────────────────────────────────────────────

export interface ProseBBTabProps {
  config: ProseBBConfig;
  sessionId: string;
  // Team consensus blocks for this roadmap.
  buildingBlocks: AllBuildingBlocks;
  // The current viewer's per-user (Solo) blocks. Used when this tab's
  // own Solo/Team toggle is set to Solo. Optional because legacy data
  // may not have a viewerOutput row — we fall back to team in that case.
  soloBuildingBlocks?: AllBuildingBlocks;
  isAdvisorOrOwner?: boolean;
  currentUserId?: string;
  /** Viewer's session role (owner|advisor|team_member|solo_contributor|comment|view);
   *  may be undefined for owners. Used to gate the collaborative team editor. */
  userRole?: string;
  companyName?: string;
  viewMode?: "solo" | "team";
  onViewModeChange?: (v: "solo" | "team") => void;
  onSessionRefresh?: () => void;
  onReportCreated?: () => void;
  visibilityMap?: VisibilityMap;
  onVisibilityChange?: (updatedMap: VisibilityMap) => void;
  onLockChange?: () => void;
  autoEdit?: boolean;
  onSendToMainChat?: (message: string) => void;
  // State 2 gate: the team-only command-bar controls (visibility / notify /
  // synthesize) only render when the roadmap has 2+ participants. In State 1
  // (single-user), showViewToggle is false and the controls stay hidden.
  showViewToggle?: boolean;
}

// ── Sub-components ──────────────────────────────────────────────────────────

function CollapsibleSection({
  title,
  icon,
  defaultExpanded = true,
  variant = "default",
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  defaultExpanded?: boolean;
  variant?: "default" | "accent" | "subtle";
  children: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const styles = {
    default: { container: "border border-slate-200 rounded-md bg-white", header: "hover:bg-slate-50", label: "text-foreground/80", icon: "text-foreground/50" },
    accent: { container: "border border-primary/20 rounded-md bg-primary/[0.03]", header: "hover:bg-primary/[0.06]", label: "text-primary/80", icon: "text-primary/50" },
    subtle: { container: "border border-slate-100 rounded-md bg-slate-50/40", header: "hover:bg-slate-50", label: "text-foreground/60", icon: "text-foreground/40" },
  }[variant];

  return (
    <div className={styles.container}>
      <button
        className={`w-full flex items-center justify-between bg-transparent border-0 cursor-pointer px-3 py-2 text-left transition-colors ${styles.header}`}
        onClick={() => setExpanded((v) => !v)}
      >
        <span className={`text-[11px] font-semibold ${styles.label} flex items-center gap-1.5`}>
          {icon && <span className={styles.icon}>{icon}</span>}
          {title}
        </span>
        {expanded ? <ChevronUp className="h-3 w-3 text-muted-foreground" /> : <ChevronDown className="h-3 w-3 text-muted-foreground" />}
      </button>
      {expanded && <div className="px-3 pb-2.5 border-t border-slate-100 pt-2">{children}</div>}
    </div>
  );
}

function DraftDisplay({ draft, contentType }: { draft: string | Record<string, string> | null; contentType: "prose" | "structured" }) {
  if (!draft) return <p className="text-xs text-muted-foreground/60 italic">No draft yet.</p>;

  if (contentType === "structured" && typeof draft === "object") {
    const fields: { key: string; label: string }[] = [
      { key: "headline", label: "Headline" },
      { key: "worldImpact", label: "World Impact" },
      { key: "businessOutcome", label: "Business Outcome" },
      { key: "pressRelease", label: "Press Release" },
      { key: "date", label: "Target Date" },
    ];
    return (
      <div className="space-y-2">
        {fields.map(({ key, label }) => {
          const val = (draft as Record<string, string>)[key];
          if (!val) return null;
          return (
            <div key={key}>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">{label}</p>
              <p className="text-xs text-foreground/80 leading-relaxed whitespace-pre-wrap">{val}</p>
            </div>
          );
        })}
      </div>
    );
  }

  return <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">{String(draft)}</p>;
}

function EditableDraftSection({
  config,
  sessionId,
  currentDraft,
  buildingBlocks,
  autoEdit,
  onSessionRefresh,
}: {
  config: ProseBBConfig;
  sessionId: string;
  currentDraft: string | Record<string, string> | null;
  buildingBlocks: AllBuildingBlocks;
  autoEdit?: boolean;
  onSessionRefresh?: () => void;
}) {
  const [isEditing, setIsEditing] = useState(!!autoEdit);
  const [editDraft, setEditDraft] = useState<string>(() =>
    typeof currentDraft === "string" ? currentDraft : ""
  );
  const [editFields, setEditFields] = useState<Record<string, string>>(() =>
    typeof currentDraft === "object" && currentDraft ? { ...currentDraft } : {}
  );
  const [isSaving, setIsSaving] = useState(false);

  const startEdit = () => {
    if (config.contentType === "structured" && typeof currentDraft === "object" && currentDraft) {
      setEditFields({ ...currentDraft });
    } else {
      setEditDraft(typeof currentDraft === "string" ? currentDraft : "");
    }
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      let updatedBlock: Record<string, unknown>;
      const existing = (buildingBlocks[config.blockKey] as unknown as Record<string, unknown>) || {};
      if (config.contentType === "structured") {
        updatedBlock = { ...existing, ...editFields };
      } else {
        updatedBlock = { ...existing, draft: editDraft };
      }
      const updatedBlocks = { ...buildingBlocks, [config.blockKey]: updatedBlock };
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/blocks`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ buildingBlocks: updatedBlocks }),
      });
      if (r.ok) {
        antdMessage.success(`${config.bbLabel} updated`);
        setIsEditing(false);
        onSessionRefresh?.();
      } else {
        antdMessage.error("Failed to save");
      }
    } catch {
      antdMessage.error("Network error");
    } finally {
      setIsSaving(false);
    }
  };

  // Auto-start edit when autoEdit is true and there's content
  useEffect(() => {
    if (autoEdit && currentDraft && !isEditing) startEdit();
  }, [autoEdit]);

  // Sync local edit state when the upstream draft changes (e.g. Solo↔Team
  // toggle swapped the source). Closes Bug E. Lazy initializers still
  // cover the first-mount case.
  useEffect(() => {
    if (typeof currentDraft === "string") {
      setEditDraft(currentDraft);
    } else if (currentDraft && typeof currentDraft === "object") {
      setEditFields({ ...currentDraft });
    }
  }, [currentDraft]);

  if (!currentDraft && !isEditing) return null;

  const structuredFields = [
    { key: "headline", label: "Headline" },
    { key: "worldImpact", label: "World Impact" },
    { key: "businessOutcome", label: "Business Outcome" },
    { key: "pressRelease", label: "Press Release" },
    { key: "date", label: "Target Date" },
  ];

  return (
    <CollapsibleSection title={`Current ${config.bbLabel}`} icon={<FileText className="h-3 w-3" />} defaultExpanded>
      <div className="flex items-center justify-between mb-2">
        <div className="flex-1" />
        <div className="flex items-center gap-1">
          {isEditing ? (
            <>
              <Button size="small" onClick={cancelEdit} style={{ fontSize: 10 }}>Cancel</Button>
              <Button size="small" type="primary" loading={isSaving} onClick={handleSave} style={{ fontSize: 10 }} icon={<Check className="h-3 w-3" />}>Save</Button>
            </>
          ) : (
            <Tooltip title={`Edit ${config.bbLabel}`}>
              <button onClick={startEdit} className="p-1 rounded hover:bg-slate-100 bg-transparent border-0 cursor-pointer text-slate-400 hover:text-primary transition-colors">
                <Pencil className="h-3 w-3" />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
      {isEditing ? (
        config.contentType === "structured" ? (
          <div className="space-y-3">
            {structuredFields.map(({ key, label }) => (
              <div key={key}>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
                {key === "pressRelease" ? (
                  <Input.TextArea
                    autoSize={{ minRows: 3, maxRows: 8 }}
                    value={editFields[key] || ""}
                    onChange={(e) => setEditFields((prev) => ({ ...prev, [key]: e.target.value }))}
                    style={{ fontSize: 12 }}
                  />
                ) : (
                  <Input
                    value={editFields[key] || ""}
                    onChange={(e) => setEditFields((prev) => ({ ...prev, [key]: e.target.value }))}
                    style={{ fontSize: 12 }}
                  />
                )}
              </div>
            ))}
          </div>
        ) : (
          <Input.TextArea
            autoSize={{ minRows: 3, maxRows: 10 }}
            value={editDraft}
            onChange={(e) => setEditDraft(e.target.value)}
            style={{ fontSize: 12 }}
          />
        )
      ) : (
        <DraftDisplay draft={currentDraft} contentType={config.contentType} />
      )}
    </CollapsibleSection>
  );
}

function MyContributionForm({
  config,
  sessionId,
  contribution,
  onSaved,
  onSendToMainChat,
}: {
  config: ProseBBConfig;
  sessionId: string;
  contribution: Contribution | null;
  onSaved: (c: Contribution) => void;
  onSendToMainChat?: (message: string) => void;
}) {
  const [rawNotes, setRawNotes] = useState(contribution?.rawNotes || "");
  const [isSaving, setIsSaving] = useState(false);
  const { isRecording, isInitializing, isProcessingAudio, startRecording, stopRecording } = useMicRecorder();

  const handleSubmit = useCallback(async (overrideText?: string) => {
    const text = overrideText ?? rawNotes;
    if (!text.trim() || isSaving) return;
    if (onSendToMainChat) {
      onSendToMainChat(`Update my ${config.bbLabel}: ${text}`);
      setRawNotes("");
      return;
    }
    setIsSaving(true);
    try {
      const url = `${SERVER_URL}/api/rb/sessions/${sessionId}/bb-prose/${config.bbType}/contributions/save`;
      const r = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ raw_notes: text }),
      });
      if (r.ok) {
        const saved: Contribution = await r.json();
        onSaved(saved);
        antdMessage.success("Contribution saved");
      } else {
        antdMessage.error("Failed to save — please try again.");
      }
    } catch (e) {
      console.error("[ProseBB] handleSubmit error", e);
      antdMessage.error("Network error — please try again.");
    } finally {
      setIsSaving(false);
    }
  }, [rawNotes, isSaving, sessionId, config.bbType, config.bbLabel, onSaved, onSendToMainChat]);

  return (
    <CollapsibleSection title={`Work with Vo on My ${config.bbLabel}`} icon={<Sparkles className="h-3 w-3" />} variant="accent" defaultExpanded>
      <VoInput
        value={rawNotes}
        onChange={setRawNotes}
        onSubmit={handleSubmit}
        isRecording={isRecording}
        isInitializing={isInitializing}
        isProcessingAudio={isProcessingAudio}
        isSubmitting={isSaving}
        placeholder={config.contributionPlaceholder}
        onStartRecording={() =>
          startRecording(RecordingObjectType.MODUS_CHAT, async (transcript) => {
            const combined = rawNotes ? `${rawNotes} ${transcript}` : transcript;
            setRawNotes(combined);
            await handleSubmit(combined);
          })
        }
        onStopRecording={stopRecording}
      />
    </CollapsibleSection>
  );
}

function TeamDraftEditForm({
  config,
  sessionId,
  onSaved,
  onSendToMainChat,
}: {
  config: ProseBBConfig;
  sessionId: string;
  onSaved: (draft: string | Record<string, string>) => void;
  onSendToMainChat?: (message: string) => void;
}) {
  const [rawNotes, setRawNotes] = useState("");
  const [isParsing, setIsParsing] = useState(false);
  const { isRecording, isInitializing, isProcessingAudio, startRecording, stopRecording } = useMicRecorder();

  const handleSubmit = useCallback(async (overrideText?: string) => {
    const text = overrideText ?? rawNotes;
    if (!text.trim() || isParsing) return;
    if (onSendToMainChat) {
      onSendToMainChat(`Update the team ${config.bbLabel}: ${text}`);
      setRawNotes("");
      return;
    }
    setIsParsing(true);
    try {
      const r = await fetch(
        `${SERVER_URL}/api/rb/sessions/${sessionId}/bb-prose/${config.bbType}/edit-draft`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ raw_notes: text }),
        }
      );
      if (r.ok) {
        const data = await r.json();
        if (data.teamDraft) {
          onSaved(data.teamDraft);
          setRawNotes("");
        } else {
          antdMessage.warning("Vo couldn't process your input — try adding more detail.");
        }
      } else {
        const err = await r.json().catch(() => ({}));
        antdMessage.error(err.detail || "Failed to save edits — please try again.");
      }
    } catch {
      antdMessage.error("Network error — please try again.");
    } finally {
      setIsParsing(false);
    }
  }, [rawNotes, isParsing, sessionId, config.bbType, config.bbLabel, onSaved, onSendToMainChat]);

  return (
    <VoInput
      value={rawNotes}
      onChange={setRawNotes}
      onSubmit={handleSubmit}
      isRecording={isRecording}
      isInitializing={isInitializing}
      isProcessingAudio={isProcessingAudio}
      isSubmitting={isParsing}
      placeholder={config.editPlaceholder}
      onStartRecording={() =>
        startRecording(RecordingObjectType.MODUS_CHAT, async (transcript) => {
          const combined = rawNotes ? `${rawNotes} ${transcript}` : transcript;
          setRawNotes(combined);
          await handleSubmit(combined);
        })
      }
      onStopRecording={stopRecording}
    />
  );
}

function SynthesisSection({
  synthesis,
  bbLabel,
  companyName,
  onDelete,
}: {
  synthesis: Synthesis;
  bbLabel: string;
  companyName?: string;
  onDelete?: () => void;
}) {
  const tarTitle = `Roadmap: ${bbLabel} Team Alignment Report`;
  const [expanded, setExpanded] = useState(false);
  const [focusOpen, setFocusOpen] = useState(false);

  return (
    <div className="border border-slate-100 rounded-md bg-slate-50/60 overflow-hidden">
      <div
        className="flex items-center px-3 py-2 cursor-pointer hover:bg-slate-100/60 transition-colors select-none"
        onClick={() => setExpanded((v) => !v)}
      >
        <FileText className="h-3 w-3 text-foreground/70 shrink-0" />
        <span className="text-[11px] font-semibold text-foreground/70 ml-1.5">{tarTitle}</span>
        <span className="text-[10px] text-muted-foreground ml-2 truncate min-w-0">
          {synthesis.generatedAt ? new Date(synthesis.generatedAt).toLocaleDateString() : ""}
        </span>
        <div className="flex-1" />
        <div className="flex items-center gap-1.5 shrink-0">
          <Tooltip title="Download as PDF">
            <button
              onClick={(e) => {
                e.stopPropagation();
                exportTarPdf(synthesis.reportMarkdown, {
                  companyName: companyName || "Roadmap Session",
                  periodLabel: bbLabel,
                  generatedAt: synthesis.generatedAt,
                });
              }}
              className="flex items-center bg-transparent border-0 p-0 cursor-pointer text-slate-300 hover:text-[#2c1561] transition-colors"
            >
              <Download className="h-3 w-3" />
            </button>
          </Tooltip>
          <Tooltip title="Focus mode">
            <button
              onClick={(e) => { e.stopPropagation(); setFocusOpen(true); }}
              className="flex items-center bg-transparent border-0 p-0 cursor-pointer text-slate-300 hover:text-[#2c1561] transition-colors"
            >
              <Maximize2 className="h-3 w-3" />
            </button>
          </Tooltip>
          {onDelete && (
            <Tooltip title="Delete report">
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="flex items-center bg-transparent border-0 p-0 cursor-pointer text-slate-300 hover:text-red-500 transition-colors"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </Tooltip>
          )}
        </div>
        {expanded
          ? <ChevronUp className="h-3 w-3 text-muted-foreground ml-2 shrink-0" />
          : <ChevronDown className="h-3 w-3 text-muted-foreground ml-2 shrink-0" />
        }
      </div>
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-violet-100">
          <div className="text-xs text-foreground/80 leading-relaxed">
            {renderMarkdown(synthesis.reportMarkdown, { baseFontSize: 11 })}
          </div>
        </div>
      )}
      <Modal
        open={focusOpen}
        onCancel={() => setFocusOpen(false)}
        footer={null}
        width={720}
        title={<span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-700" /> {tarTitle}</span>}
      >
        <div className="text-sm text-foreground/80 leading-relaxed max-h-[70vh] overflow-y-auto py-2">
          {renderMarkdown(synthesis.reportMarkdown, { baseFontSize: 13 })}
        </div>
      </Modal>
    </div>
  );
}

function NotifyModal({
  open,
  onClose,
  config,
  sessionId,
}: {
  open: boolean;
  onClose: () => void;
  config: ProseBBConfig;
  sessionId: string;
}) {
  const [deadline, setDeadline] = useState<dayjs.Dayjs | null>(null);
  const [message, setMessage] = useState(`Please submit your proposed ${config.bbLabel.toLowerCase()} by the deadline above.`);
  const [emails, setEmails] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (open) { setSent(false); setEmailError(null); }
  }, [open]);

  const handleSend = async () => {
    const rawEmails = emails.split(/[\n,]+/).map((e) => e.trim()).filter(Boolean);
    if (!rawEmails.length) { setEmailError("Add at least one email address to notify."); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalid = rawEmails.filter((e) => !emailRegex.test(e));
    if (invalid.length) { setEmailError(`Invalid email${invalid.length > 1 ? "s" : ""}: ${invalid.join(", ")}`); return; }
    setEmailError(null);
    setSending(true);
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb-prose/${config.bbType}/rounds/notify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          deadline: deadline?.toISOString() || null,
          message,
          participants: rawEmails.map((email) => ({ email })),
          send_notifications: true,
        }),
      });
      if (r.ok) { setSent(true); setTimeout(onClose, 1500); }
    } catch { /* ignore */ }
    finally { setSending(false); }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={`Notify Team — ${config.bbLabel}`}
      footer={sent ? null : [
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="send" type="primary" loading={sending} onClick={handleSend}>Send Notification</Button>,
      ]}
    >
      {sent ? (
        <div className="py-6 text-center text-sm text-green-600 font-medium">Notifications sent!</div>
      ) : (
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Team emails <span className="text-red-500">*</span></label>
            <Input.TextArea value={emails} onChange={(e) => { setEmails(e.target.value); setEmailError(null); }} rows={3} placeholder="one@example.com, two@example.com" />
            {emailError && <p className="text-xs text-red-500 mt-1">{emailError}</p>}
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Submission deadline (optional)</label>
            <DatePicker value={deadline} onChange={setDeadline} style={{ width: "100%" }} placeholder="No deadline" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Personal message</label>
            <Input.TextArea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} />
          </div>
        </div>
      )}
    </Modal>
  );
}

function ContributorList({ contributions }: { contributions: Contribution[] }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  if (contributions.length === 0) {
    return <p className="text-xs text-muted-foreground/60 italic text-center py-3">No contributions yet.</p>;
  }
  return (
    <div className="space-y-2">
      {contributions.map((c) => (
        <div key={c.id} className="border border-slate-100 rounded-md overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-3 py-2 bg-transparent border-0 cursor-pointer text-left hover:bg-slate-50/50"
            onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
          >
            <span className="text-xs font-medium">{c.contributorName}</span>
            {expandedId === c.id ? <ChevronUp className="h-3 w-3 text-muted-foreground" /> : <ChevronDown className="h-3 w-3 text-muted-foreground" />}
          </button>
          {expandedId === c.id && (
            <div className="px-3 pb-3 border-t border-slate-100 pt-2">
              <p className="text-xs text-foreground/70 leading-relaxed whitespace-pre-wrap">{c.rawNotes}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function VersionedTeamDraft({
  config,
  sessionId,
  isAdvisorOrOwner,
  onSessionRefresh,
  refreshKey,
}: {
  config: ProseBBConfig;
  sessionId: string;
  isAdvisorOrOwner?: boolean;
  onSessionRefresh?: () => void;
  refreshKey: number;
}) {
  const [versions, setVersions] = useState<ProseVersion[]>([]);
  const [viewIndex, setViewIndex] = useState(-1);
  const [restoring, setRestoring] = useState(false);
  // Collapsed by default: the live editor above is the current team draft;
  // this section is the audit/restore trail, not a competing draft view.
  const [expanded, setExpanded] = useState(false);

  const loadVersions = useCallback(async () => {
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb-prose/${config.bbType}/versions`, { credentials: "include" });
      if (r.ok) {
        const data = await r.json();
        const v: ProseVersion[] = data.versions || [];
        setVersions(v);
        if (v.length > 0) setViewIndex(v.length - 1);
      }
    } catch { /* ignore */ }
  }, [sessionId, config.bbType]);

  useEffect(() => { loadVersions(); }, [loadVersions, refreshKey]);

  const current = viewIndex >= 0 && viewIndex < versions.length ? versions[viewIndex] : null;
  const isLatest = viewIndex === versions.length - 1;
  const total = versions.length;

  const handleRestore = async () => {
    if (!current || isLatest) return;
    setRestoring(true);
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb-prose/${config.bbType}/versions/restore`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ version: current.version }),
      });
      if (r.ok) {
        antdMessage.success(`Restored version ${current.version} as new version`);
        await loadVersions();
        onSessionRefresh?.();
      }
    } catch { antdMessage.error("Restore failed"); }
    finally { setRestoring(false); }
  };

  const sourceLabel = (src: string) => {
    if (src === "synthesis") return "Vo synthesis";
    if (src === "direct_edit") return "Edited with Vo";
    if (src.startsWith("restored_from_v")) return `Restored from v${src.replace("restored_from_v", "")}`;
    return src;
  };

  if (total === 0) return null;

  return (
    <div className="border border-slate-100 rounded-md bg-slate-50/60 overflow-hidden">
      <div
        className="flex items-center px-3 py-2 cursor-pointer select-none"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="text-[11px] font-semibold text-foreground/70 flex items-center gap-1.5">
          <FileText className="h-3 w-3" />
          Version history
        </span>
        <div className="flex-1" />
        {total > 1 && (
          <div className="flex items-center gap-1 text-[10px] text-foreground/60 mr-2" onClick={(e) => e.stopPropagation()}>
            <button
              className="p-0.5 rounded hover:bg-slate-200 disabled:opacity-30 bg-transparent border-0 cursor-pointer"
              disabled={viewIndex <= 0}
              onClick={() => setViewIndex((v) => v - 1)}
            >
              <ChevronLeft className="h-3 w-3" />
            </button>
            <span className="font-medium tabular-nums">v{viewIndex + 1}/{total}</span>
            <button
              className="p-0.5 rounded hover:bg-slate-200 disabled:opacity-30 bg-transparent border-0 cursor-pointer"
              disabled={viewIndex >= total - 1}
              onClick={() => setViewIndex((v) => v + 1)}
            >
              <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        )}
        {!isLatest && isAdvisorOrOwner && (
          <Tooltip title={`Restore v${viewIndex + 1} as new version`}>
            <button onClick={(e) => { e.stopPropagation(); handleRestore(); }} disabled={restoring} className="p-1 rounded hover:bg-slate-200 bg-transparent border-0 cursor-pointer text-slate-400 hover:text-primary transition-colors mr-0.5">
              <RotateCcw className={`h-3 w-3 ${restoring ? "animate-spin" : ""}`} />
            </button>
          </Tooltip>
        )}
        {expanded
          ? <ChevronUp className="h-3 w-3 text-muted-foreground ml-1.5 shrink-0" />
          : <ChevronDown className="h-3 w-3 text-muted-foreground ml-1.5 shrink-0" />
        }
      </div>
      {expanded && (
        <div className="px-3 py-2.5 space-y-2 border-t border-slate-100">
          {current && (
            <p className="text-[10px] text-muted-foreground/60 flex items-center gap-1">
              {sourceLabel(current.source)} — {new Date(current.createdAt).toLocaleString()}{current.editedAt && " · edited"}
            </p>
          )}
          {current && <DraftDisplay draft={current.draft} contentType={config.contentType} />}
        </div>
      )}
    </div>
  );
}

function VersionedSoloDraft({
  config,
  sessionId,
  onSessionRefresh,
  refreshKey,
  buildingBlocks,
}: {
  config: ProseBBConfig;
  sessionId: string;
  onSessionRefresh?: () => void;
  refreshKey: number;
  buildingBlocks?: AllBuildingBlocks;
}) {
  const [versions, setVersions] = useState<ProseVersion[]>([]);
  const [viewIndex, setViewIndex] = useState(-1);
  const [restoring, setRestoring] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const blockFingerprint = JSON.stringify(buildingBlocks?.[config.blockKey] ?? null);

  const loadVersions = useCallback(async () => {
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/solo/bb-prose/${config.bbType}/versions`, { credentials: "include" });
      if (r.ok) {
        const data = await r.json();
        const v: ProseVersion[] = data.versions || [];
        setVersions(v);
        if (v.length > 0) setViewIndex(v.length - 1);
      }
    } catch { /* ignore */ }
  }, [sessionId, config.bbType]);

  useEffect(() => { loadVersions(); }, [loadVersions, refreshKey, blockFingerprint]);

  const current = viewIndex >= 0 && viewIndex < versions.length ? versions[viewIndex] : null;
  const isLatest = viewIndex === versions.length - 1;
  const total = versions.length;

  const handleRestore = async () => {
    if (!current || isLatest) return;
    setRestoring(true);
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/solo/bb-prose/${config.bbType}/versions/restore`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ version: current.version }),
      });
      if (r.ok) {
        antdMessage.success(`Restored version ${current.version} as new version`);
        await loadVersions();
        onSessionRefresh?.();
      }
    } catch { antdMessage.error("Restore failed"); }
    finally { setRestoring(false); }
  };

  const sourceLabel = (src: string) => {
    if (src === "vo_chat") return "Vo chat";
    if (src === "manual_edit") return "Manual edit";
    if (src === "synthesis") return "Vo synthesis";
    if (src === "direct_edit") return "Edited with Vo";
    if (src.startsWith("restored_from_v")) return `Restored from v${src.replace("restored_from_v", "")}`;
    return src;
  };

  if (total === 0) return null;

  return (
    <div className="border border-slate-100 rounded-md bg-slate-50/60 overflow-hidden">
      <div
        className="flex items-center px-3 py-2 cursor-pointer select-none"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="text-[11px] font-semibold text-foreground/70 flex items-center gap-1.5">
          <FileText className="h-3 w-3" />
          My {config.bbLabel}
        </span>
        <div className="flex-1" />
        {total > 1 && (
          <div className="flex items-center gap-1 text-[10px] text-foreground/60 mr-2" onClick={(e) => e.stopPropagation()}>
            <button
              className="p-0.5 rounded hover:bg-slate-200 disabled:opacity-30 bg-transparent border-0 cursor-pointer"
              disabled={viewIndex <= 0}
              onClick={() => setViewIndex((v) => v - 1)}
            >
              <ChevronLeft className="h-3 w-3" />
            </button>
            <span className="font-medium tabular-nums">v{viewIndex + 1}/{total}</span>
            <button
              className="p-0.5 rounded hover:bg-slate-200 disabled:opacity-30 bg-transparent border-0 cursor-pointer"
              disabled={viewIndex >= total - 1}
              onClick={() => setViewIndex((v) => v + 1)}
            >
              <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        )}
        {!isLatest && (
          <Tooltip title={`Restore v${viewIndex + 1} as new version`}>
            <button onClick={(e) => { e.stopPropagation(); handleRestore(); }} disabled={restoring} className="p-1 rounded hover:bg-slate-200 bg-transparent border-0 cursor-pointer text-slate-400 hover:text-primary transition-colors mr-0.5">
              <RotateCcw className={`h-3 w-3 ${restoring ? "animate-spin" : ""}`} />
            </button>
          </Tooltip>
        )}
        {expanded
          ? <ChevronUp className="h-3 w-3 text-muted-foreground ml-1.5 shrink-0" />
          : <ChevronDown className="h-3 w-3 text-muted-foreground ml-1.5 shrink-0" />
        }
      </div>
      {expanded && (
        <div className="px-3 py-2.5 space-y-2 border-t border-slate-100">
          {current && (
            <p className="text-[10px] text-muted-foreground/60 flex items-center gap-1">
              {sourceLabel(current.source)} — {new Date(current.createdAt).toLocaleString()}{current.editedAt && " · edited"}
            </p>
          )}
          {current && <DraftDisplay draft={current.draft} contentType={config.contentType} />}
        </div>
      )}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

export function ProseBBTab({
  config,
  sessionId,
  buildingBlocks,
  soloBuildingBlocks,
  isAdvisorOrOwner,
  currentUserId,
  userRole,
  companyName,
  viewMode: propViewMode,
  onSessionRefresh,
  onReportCreated,
  visibilityMap: parentVisMap,
  onVisibilityChange,
  onLockChange,
  autoEdit,
  onSendToMainChat,
  showViewToggle = false,
}: ProseBBTabProps) {
  const [isLocked, setIsLocked] = useState(false);
  const [myContribution, setMyContribution] = useState<Contribution | null>(null);
  const [allContributions, setAllContributions] = useState<Contribution[]>([]);
  const [periodMeta, setPeriodMeta] = useState<PeriodMeta | null>(null);
  const [synthesis, setSynthesis] = useState<Synthesis | null>(null);
  const [isSynthesizing, setIsSynthesizing] = useState(false);
  const [synthStream, setSynthStream] = useState("");
  const [synthError, setSynthError] = useState<string | null>(null);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [synthConfirmOpen, setSynthConfirmOpen] = useState(false);
  const [committedOnly, setCommittedOnly] = useState(false);
  const [locking, setLocking] = useState(false);
  const [visMap, setVisMap] = useState<VisibilityMap>(parentVisMap ?? {});
  const [versionsRefreshKey, setVersionsRefreshKey] = useState(0);

  // Imperative handle to the live team collaborative editor (vision/mission).
  // Null when the editor isn't mounted (solo view, structured peakAmbition, or
  // the "team data not yet available" state) — every call site uses `?.`.
  const collabEditorRef = useRef<CollaborativeProseEditorHandle>(null);

  useEffect(() => { if (parentVisMap) setVisMap(parentVisMap); }, [parentVisMap]);

  useEffect(() => {
    fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/visibility`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setVisMap(data); })
      .catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((s) => {
        if (s?.lockedBlocks?.[config.blockKey]) setIsLocked(true);
        else setIsLocked(false);
      })
      .catch(() => {});
  }, [sessionId, config.blockKey]);

  // Fetch the Team Contributions list. Extracted so we can refetch when the
  // viewer builds/commits their own block or after synthesis — otherwise the
  // mount-time list goes stale and a just-committed block never appears until
  // a remount (the bug Ross hit: owner's committed block missing until synthesis).
  const loadContributions = useCallback(() => {
    fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb-prose/${config.bbType}/contributions`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((docs: Contribution[] | null) => {
        if (!docs) return;
        setAllContributions(docs);
        if (currentUserId) setMyContribution(docs.find((d) => d.contributorUserId === currentUserId) || null);
      })
      .catch(() => {});
  }, [sessionId, config.bbType, currentUserId]);

  // Load contributions + period meta (reset synthesis state on block/session switch)
  useEffect(() => {
    setPeriodMeta(null);
    setSynthesis(null);
    setMyContribution(null);
    setAllContributions([]);

    fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb-prose/${config.bbType}/rounds`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d) setPeriodMeta(d); })
      .catch(() => {});

    loadContributions();
  }, [sessionId, currentUserId, config.bbType, loadContributions]);

  // Refetch contributions when the viewer's OWN block content changes (built or
  // committed a draft) so the count self-heals without a remount.
  const ownBlockFingerprint = JSON.stringify(soloBuildingBlocks?.[config.blockKey] ?? null);
  useEffect(() => {
    loadContributions();
  }, [ownBlockFingerprint, loadContributions]);

  // Load synthesis. Prefer the synthesis_summary pointer, but fall back to the last
  // entry of synthesisReports[] — some rounds have a report in the history array
  // without the summary pointer set, which otherwise leaves the TAR unloadable on
  // reload even though Studio (which reads the reports directly) still shows it.
  useEffect(() => {
    const reports = periodMeta?.synthesisReports;
    const reportId = periodMeta?.synthesisSummary?.reportId
      ?? (reports && reports.length > 0 ? reports[reports.length - 1].reportId : undefined);
    if (!reportId) return;
    fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb-prose/${config.bbType}/synthesis/${reportId}`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d) setSynthesis(d); })
      .catch(() => {});
  }, [sessionId, periodMeta?.synthesisSummary?.reportId, periodMeta?.synthesisReports, config.bbType]);

  // Actions
  const doSynthesis = async () => {
    setIsSynthesizing(true);
    setSynthStream("");
    setSynthError(null);
    try {
      const response = await fetch(
        `${SERVER_URL}/api/rb/sessions/${sessionId}/bb-prose/${config.bbType}/synthesize`,
        { method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ committedOnly }) }
      );
      if (!response.ok) {
        setSynthError(`Synthesis failed (${response.status})`);
        setIsSynthesizing(false);
        return;
      }
      if (!response.body) { setSynthError("No response body"); setIsSynthesizing(false); return; }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let reportId = "";
      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;
        const chunk = decoder.decode(value, { stream: true });
        for (const line of chunk.split("\n")) {
          if (!line.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(line.slice(6));
            if (payload.error) { setSynthError(payload.error); continue; }
            if (payload.content) setSynthStream((prev) => prev + payload.content);
            if (payload.done) {
              reportId = payload.reportId;
              const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb-prose/${config.bbType}/rounds`, { credentials: "include" });
              if (r.ok) setPeriodMeta(await r.json());
              if (reportId) {
                const sr = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb-prose/${config.bbType}/synthesis/${reportId}`, { credentials: "include" });
                if (sr.ok) {
                  const synthData: Synthesis = await sr.json();
                  setSynthesis(synthData);
                  // Load the synthesized team draft into the live editor as a
                  // new version. String-only (vision/mission); structured
                  // peakAmbition is handled in a later task. Backend already
                  // appended the prior draft to {block}_prose_versions, so
                  // version history is preserved. Ref is null in solo/structured
                  // views — `?.` no-ops there.
                  if (typeof synthData.teamDraft === "string") {
                    collabEditorRef.current?.replaceContent(synthData.teamDraft);
                  }
                }
              }
              setVersionsRefreshKey((k) => k + 1);
              loadContributions();
              onSessionRefresh?.();
              onReportCreated?.();
            }
          } catch (parseErr) { console.warn("SSE parse error:", parseErr); }
        }
      }
    } catch (err) {
      setSynthError(`Synthesis request failed: ${err instanceof Error ? err.message : "Network error"}`);
    } finally { setIsSynthesizing(false); }
  };

  const handleLockToggle = async () => {
    setLocking(true);
    try {
      const endpoint = isLocked ? "unlock" : "lock";
      const r = await fetch(
        `${SERVER_URL}/api/rb/sessions/${sessionId}/bb-prose/${config.bbType}/${endpoint}`,
        { method: "POST", credentials: "include" }
      );
      if (r.ok) {
        setIsLocked(!isLocked);
        onSessionRefresh?.();
        onLockChange?.();
      }
    } catch { /* ignore */ }
    finally { setLocking(false); }
  };

  const handleVisibilityToggle = async () => {
    const mapKey = `${config.periodType}:bb-${config.bbType}`;
    const effectiveVis = isVisibleForPeriod(visMap, config.periodType, `bb-${config.bbType}`);
    const newVis = effectiveVis ? "gated" : "open";
    const updatedMap = { ...visMap, [mapKey]: newVis as "open" | "gated" };
    setVisMap(updatedMap);
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/visibility`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ period_type: config.periodType, period_key: `bb-${config.bbType}`, visibility: newVis }),
      });
      if (r.ok) { const serverMap = await r.json(); setVisMap(serverMap); onVisibilityChange?.(serverMap); }
    } catch { /* ignore */ }
  };

  // Derived state
  const visibilityOpen = isVisibleForPeriod(visMap, config.periodType, `bb-${config.bbType}`);

  // Effective role for the collaborative team editor's edit/comment gating.
  // An undefined userRole on an owner session means owner (RoadmapBuilderSession
  // treats `!userRole` as owner/advisor — see its line ~979).
  const role = userRole || (isAdvisorOrOwner ? "owner" : "team_member");

  // The TAR (SynthesisSection) now renders inline in BOTH views (see top of the
  // scroll area), so we no longer force-flip to Team view on synthesis — the
  // block stays in whatever view the global toggle selects. (Superseded the old
  // hasSynthesisForPeriod flip; #821 "Fix TAR not showing inline on reload".)
  const effectiveViewMode: "solo" | "team" = propViewMode ?? "solo";

  // Solo mode reads from the viewer's own RoadmapOutput slice; Team
  // reads the consensus on the parent session. Falls back to team if
  // soloBuildingBlocks is missing (legacy data with no viewerOutput).
  const activeBuildingBlocks: AllBuildingBlocks =
    effectiveViewMode === "solo"
      ? (soloBuildingBlocks ?? buildingBlocks)
      : buildingBlocks;
  const currentBlock = activeBuildingBlocks[config.blockKey];

  // Memoize so reference stays stable when content does — the
  // EditableDraftSection useEffect depends on this and would otherwise
  // fire every render of the parent (and clobber in-flight edits).
  // Mode is in the dependency list so flipping Solo↔Team produces a
  // fresh reference, the effect fires, and the editor input swaps to
  // the other slice's draft (closes the "have to refresh" bug).
  const currentDraft = useMemo(() => {
    if (config.contentType === "structured") {
      const pa = currentBlock as PeakAmbition;
      if (pa.headline || pa.worldImpact || pa.businessOutcome || pa.pressRelease) {
        return { headline: pa.headline, worldImpact: pa.worldImpact, businessOutcome: pa.businessOutcome, pressRelease: pa.pressRelease, date: pa.date };
      }
      return pa.draft || null;
    }
    return (currentBlock as { draft?: string }).draft || null;
  }, [
    config.contentType,
    effectiveViewMode,
    (currentBlock as PeakAmbition | undefined)?.headline,
    (currentBlock as PeakAmbition | undefined)?.worldImpact,
    (currentBlock as PeakAmbition | undefined)?.businessOutcome,
    (currentBlock as PeakAmbition | undefined)?.pressRelease,
    (currentBlock as PeakAmbition | undefined)?.date,
    (currentBlock as { draft?: string } | undefined)?.draft,
  ]);

  // ── Render ────────────────────────────────────────────────────────────────

  // Team-only controls only render in State 2 (2+ participants, showViewToggle
  // true). In State 1 the config is empty so ConsensusBuilderBar suppresses them.
  const commandCenterConfig: CommandCenterConfig = showViewToggle ? {
    visibility: {
      isVisible: visibilityOpen,
      onToggle: handleVisibilityToggle,
      tooltipVisible: `Team data for ${config.bbLabel} visible to contributors — click to hide`,
      tooltipHidden: `Team data for ${config.bbLabel} hidden from contributors — click to show`,
    },
    // Notify + Synthesize act on the TEAM version, and the synthesized draft only
    // renders in Team view (the team editor is mounted there). Offer them in Team
    // view only, so the owner never "synthesizes" from Solo and sees nothing.
    ...(effectiveViewMode === "team" ? {
      notify: { onClick: () => setNotifyOpen(true) },
      synthesize: { onClick: () => setSynthConfirmOpen(true), isSynthesizing },
    } : {}),
  } : {};

  return (
    <div className="h-full flex flex-col">
      <ConsensusBuilderBar
        isAdvisorOrOwner={isAdvisorOrOwner}
        showToggle={false}
        controls={commandCenterConfig}
      />

      <div className="overflow-y-auto px-3 py-3 space-y-4 flex-1">

      {/* Team Alignment Report — rendered inline whenever a synthesis exists, in
          BOTH Solo and Team view (the TAR is shared team output). Previously it
          lived only in the team branch, so it vanished on reload when the block sat
          in the default Solo view. Still gated so contributors only see it once
          the owner shares team data (visibilityOpen); owner/advisor always. */}
      {synthesis && !isSynthesizing && (isAdvisorOrOwner || visibilityOpen) && (
        <SynthesisSection
          synthesis={synthesis}
          bbLabel={config.bbLabel}
          companyName={companyName}
          onDelete={isAdvisorOrOwner ? async () => {
            try {
              await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/alignment-reports/${synthesis.id}`, { method: "DELETE", credentials: "include" });
              setSynthesis(null);
              const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb-prose/${config.bbType}/rounds`, { credentials: "include" });
              if (r.ok) {
                const rd = await r.json();
                setPeriodMeta(rd);
                const reports = rd.synthesisReports || [];
                if (reports.length > 0) {
                  const lastId = reports[reports.length - 1].reportId;
                  const sr = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb-prose/${config.bbType}/synthesis/${lastId}`, { credentials: "include" });
                  if (sr.ok) setSynthesis(await sr.json());
                }
              }
              antdMessage.success("Report deleted");
              onReportCreated?.();
            } catch { antdMessage.error("Failed to delete report"); }
          } : undefined}
        />
      )}

      {/* LOCKED STATE */}
      {isLocked ? (
        <div className="space-y-3">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-600">
            <Lock className="h-3 w-3" />
            {config.bbLabel} locked — finalized
          </div>
          <DraftDisplay draft={currentDraft} contentType={config.contentType} />
          {isAdvisorOrOwner && (
            <div className="border-t border-slate-100 pt-4 mt-2">
              <Button
                block
                onClick={handleLockToggle}
                loading={locking}
                icon={<Unlock className="h-4 w-4" />}
                style={{ height: 40, fontWeight: 600, fontSize: 13, borderRadius: 8, borderColor: "#d97706", color: "#92400e", background: "#fffbeb" }}
              >
                Unlock {config.bbLabel}
              </Button>
            </div>
          )}
        </div>
      ) : effectiveViewMode === "solo" ? (
        <>
          <EditableDraftSection
            config={config}
            sessionId={sessionId}
            currentDraft={currentDraft}
            buildingBlocks={activeBuildingBlocks}
            autoEdit={autoEdit}
            onSessionRefresh={onSessionRefresh}
          />
          <MyContributionForm
            config={config}
            sessionId={sessionId}
            contribution={myContribution}
            onSendToMainChat={onSendToMainChat}
            onSaved={(saved) => {
              setMyContribution(saved);
              setAllContributions((prev) => {
                const idx = prev.findIndex((d) => d.contributorUserId === saved.contributorUserId);
                return idx >= 0 ? prev.map((d, i) => i === idx ? saved : d) : [...prev, saved];
              });
            }}
          />
          <VersionedSoloDraft
            key={`vsd-${versionsRefreshKey}`}
            config={config}
            sessionId={sessionId}
            onSessionRefresh={onSessionRefresh}
            refreshKey={versionsRefreshKey}
            buildingBlocks={activeBuildingBlocks}
          />
        </>
      ) : (
        <>
          {!isAdvisorOrOwner && !visibilityOpen ? (
            <div className="flex flex-col items-center justify-center py-8 gap-2 text-center">
              <EyeOff className="h-6 w-6 text-muted-foreground/40" />
              <p className="text-sm font-medium text-foreground/60">Team data is not yet available</p>
              <p className="text-xs text-muted-foreground/50 max-w-xs">The advisor will share team contributions and synthesis when ready.</p>
            </div>
          ) : (
            <>
              {(() => {
                const visible = isAdvisorOrOwner ? allContributions : allContributions.filter((c) => c.contributorUserId === currentUserId);
                return (
                  <CollapsibleSection
                    title={isAdvisorOrOwner ? `Team Contributions (${visible.length})` : "My Contribution"}
                    icon={<Users className="h-3 w-3" />}
                    defaultExpanded={false}
                  >
                    {visible.length > 0 ? (
                      <ContributorList contributions={visible} />
                    ) : (
                      <p className="text-xs text-muted-foreground/60 italic py-2">
                        {isAdvisorOrOwner
                          ? "No contributions yet. Team members can submit from the Solo view."
                          : "You haven't submitted a contribution yet. Switch to Solo view to propose yours."}
                      </p>
                    )}
                  </CollapsibleSection>
                );
              })()}
              {synthError && (
                <div className="border border-red-200 rounded-md bg-red-50 p-3 text-xs text-red-700">
                  <p className="font-semibold mb-1">Synthesis Error</p>
                  <p>{synthError}</p>
                </div>
              )}
              {isSynthesizing && synthStream && (
                <div className="border border-violet-100 rounded-md bg-violet-50/20 p-3">
                  <p className="text-[10px] font-semibold text-violet-600 mb-1.5 flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" /> Vo is synthesizing…
                  </p>
                  <div className="text-xs text-foreground/70 leading-relaxed">
                    {renderMarkdown(synthStream, { baseFontSize: 11 })}
                  </div>
                </div>
              )}
              {!synthesis && !isSynthesizing && !synthError && (
                <p className="text-xs text-muted-foreground/60 italic">
                  {isAdvisorOrOwner
                    ? `No synthesis yet. Use "Synthesize with Vo" to generate the team ${config.bbLabel.toLowerCase()} from contributions.`
                    : "Team synthesis not yet available."}
                </p>
              )}
              {/* Live team draft — the primary, always-on collaborative surface.
                  This editor IS the team draft; the static "Team Consensus
                  Draft" display was removed (it duplicated this). Readers
                  (view/comment roles, locked blocks) still see the current draft
                  via the editor's read-only fallbackContent. */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-semibold text-foreground/70 flex items-center gap-1.5">
                    <FileText className="h-3 w-3 text-foreground/50" />
                    Team {config.bbLabel}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600 bg-emerald-50 rounded-full px-1.5 py-0.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Live
                  </span>
                </div>
                {/* vision/mission (prose): one editor. It self-sizes — short by
                    default, grows with text, caps + scrolls at max-h. */}
                {config.contentType === "prose" && (
                  <TeamProseCollabEditor
                    ref={collabEditorRef}
                    sessionId={sessionId}
                    blockKey={config.blockKey as "vision" | "mission"}
                    role={role}
                    isLocked={isLocked}
                    fallbackDraft={typeof currentDraft === "string" ? currentDraft : ""}
                  />
                )}
                {/* peakAmbition (structured): three per-subfield editors
                    (worldImpact, businessOutcome, pressRelease). headline + date
                    are read-only here; their write path remains "Revise with Vo"
                    below. replaceContent wiring for these is deferred to E1. */}
                {config.contentType === "structured" && (
                  <PeakAmbitionCollabEditor
                    sessionId={sessionId}
                    role={role}
                    isLocked={isLocked}
                    peakAmbition={currentBlock as PeakAmbition}
                  />
                )}
              </div>
              {/* Revise with Vo — natural-language rewrite of the team draft.
                  Writes a new version that loads into the editor above via
                  replaceContent. Advisor/owner-only; also the sole write path for
                  peakAmbition headline/date (the editor renders those read-only). */}
              {isAdvisorOrOwner && (
                <CollapsibleSection title="Revise with Vo" icon={<Sparkles className="h-3 w-3" />} variant="accent" defaultExpanded>
                  <TeamDraftEditForm
                    config={config}
                    sessionId={sessionId}
                    onSendToMainChat={onSendToMainChat}
                    onSaved={(draft) => {
                      setVersionsRefreshKey((k) => k + 1);
                      fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb-prose/${config.bbType}/rounds`, { credentials: "include" })
                        .then((r) => r.ok ? r.json() : null)
                        .then((rd) => { if (rd) setPeriodMeta(rd); });
                      onSessionRefresh?.();
                      antdMessage.success("Team draft updated");
                      // Load the Vo-edited draft into the live editor as a new
                      // version (string-only: vision/mission). Backend already
                      // appended the prior draft to version history. Ref no-ops
                      // when the editor isn't mounted.
                      if (typeof draft === "string") {
                        collabEditorRef.current?.replaceContent(draft);
                      }
                    }}
                  />
                </CollapsibleSection>
              )}
              <VersionedTeamDraft
                key={`vtd-${versionsRefreshKey}`}
                config={config}
                sessionId={sessionId}
                isAdvisorOrOwner={isAdvisorOrOwner}
                onSessionRefresh={onSessionRefresh}
                refreshKey={versionsRefreshKey}
              />
              {isAdvisorOrOwner && (
                <div className="border-t border-slate-100 pt-4 mt-2">
                  <Button
                    block
                    onClick={handleLockToggle}
                    loading={locking}
                    icon={isLocked ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                    style={{
                      height: 40,
                      fontWeight: 600,
                      fontSize: 13,
                      borderRadius: 8,
                      ...(isLocked
                        ? { borderColor: "#d97706", color: "#92400e", background: "#fffbeb" }
                        : { borderColor: "#2c1561", color: "#2c1561", background: "#f5f3ff" }),
                    }}
                  >
                    {isLocked ? `Unlock ${config.bbLabel}` : `Lock ${config.bbLabel} — Finalize Team Draft`}
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}

      <NotifyModal open={notifyOpen} onClose={() => setNotifyOpen(false)} config={config} sessionId={sessionId} />
      <Modal
        open={synthConfirmOpen}
        onCancel={() => setSynthConfirmOpen(false)}
        title={<span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-600" /> Synthesize with Vo</span>}
        okText="Run Synthesis"
        onOk={() => { setSynthConfirmOpen(false); doSynthesis(); }}
      >
        <p className="text-sm text-foreground/70">
          Vo will analyze all contributions and create a new <strong>team {config.bbLabel.toLowerCase()} draft</strong>, loading it into the live editor above. Any existing draft will be preserved in version history.
        </p>
        <div className="mt-4 pt-3 border-t border-slate-100">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            What should Vo synthesize?
          </div>
          <Radio.Group
            value={committedOnly}
            onChange={(e) => setCommittedOnly(e.target.value)}
            className="flex flex-col gap-1.5"
          >
            <Radio value={false}>
              <span className="text-sm font-medium">All contributions</span>
              <span className="text-xs text-muted-foreground ml-1.5">drafts + committed</span>
            </Radio>
            <Radio value={true}>
              <span className="text-sm font-medium">Committed views only</span>
              <span className="text-xs text-muted-foreground ml-1.5">views a contributor marked ready</span>
            </Radio>
          </Radio.Group>
        </div>
      </Modal>
      </div>
    </div>
  );
}
